import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faEnvelope, faPhone } from '@fortawesome/pro-light-svg-icons';
import { useForm } from 'react-hook-form';
import { tv } from 'tailwind-variants';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { Button } from '@/components/ui/button/button.component';
import { FormField } from '@/components/ui/form.component';
import { Input } from '@/components/ui/input/input.component';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { AppointmentOptionsModalKeys } from '@/app/(main)/(pages)/(auth)/agendamentos/_components/card-appointment/variants/_components/appointment-options/appointment-options-button.component';
import { generateWhatsAppText } from '@/app/(main)/(pages)/(auth)/agendamentos/_components/card-appointment/variants/_components/appointment-options/_services/appointment-share.service';
import { useLoading } from '@/hooks/use-loading.hook';
import { useToast } from '@/components/ui/use-toast';
import { generateLink } from '@/utils/formatters/generate-link';
import { Appointment } from '@/types/appointments.types';
import { useProgressiveModal } from '@/components/ui/progressive-modal/progressive-modal.hook';
import { shareAppointmentByEmail } from '@/services/appointment/share-appointment-by-email.service';

const styles = tv({
  slots: {
    form: 'flex gap-2',
    input: 'basis-4/5',
    buttonSubmit: 'basis-1/5 h-12',
    buttonBack: 'mt-5 hover:no-underline',
    accordionItem: 'flex flex-col gap-4',
    accordionTrigger: 'flex justify-start gap-2 text-green-500',
  },
});

interface AppointmentOptionsShareProps {
  appointment: Appointment;
}

const whatsappSchema = z.object({
  phone: z.string(),
});

const emailSchema = z.object({
  email: z.string().email('E-mail inválido'),
});

export function AppointmentOptionsShare({ appointment }: AppointmentOptionsShareProps) {
  const { handleJump: jump } = useProgressiveModal();
  const { toast } = useToast();
  const { form, input, buttonSubmit, buttonBack, accordionItem, accordionTrigger } = styles();

  const { id } = appointment;
  const { isLoading, startLoading, stopLoading } = useLoading();
  const whatsappForm = useForm({
    resolver: zodResolver(whatsappSchema),
    defaultValues: {
      phone: '',
    },
  });
  const emailForm = useForm({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      email: '',
    },
  });

  const onHandleSubmitWhatsApp = whatsappForm.handleSubmit((values) => {
    const msg = generateWhatsAppText(appointment);

    const whatsappUrl = generateLink({
      url: 'https://api.whatsapp.com/send',
      params: [
        {
          key: 'phone',
          value: `+55${values.phone.replace(/\D/g, '')}`,
        },
        {
          key: 'text',
          value: `Olá! Segue guia do meu agendamento: ${msg} `,
        },
      ],
    });

    window.open(whatsappUrl, '_blank');
    jump({ key: 'CLOSE', returning: true });
    toast.success('Agendamento compartilhado via WhatsApp');
  });

  const onHandleSubmitEmail = emailForm.handleSubmit(async (values) => {
    try {
      startLoading();
      await shareAppointmentByEmail(id, values.email);
      jump({ key: 'CLOSE', returning: true });
      toast.success('Agendamento compartilhado via E-mail');
    } catch {
      toast.error('Erro ao gerar guia para compartilhar.');
    } finally {
      stopLoading();
    }
  });

  return (
    <Accordion type="single" collapsible className={accordionItem()}>
      <AccordionItem value="whatsapp" className={accordionItem()}>
        <AccordionTrigger className={accordionTrigger()} startIcon={false} showNoIcon={true} underlineOnHover={false}>
          <>
            <FontAwesomeIcon icon={faWhatsapp} size="lg" />
            <Typography variant={TypographyVariant.BODY_TEXT} className="ml-2">Enviar por WhatsApp</Typography>
          </>
        </AccordionTrigger>
        <AccordionContent>
          <form className={form()} onSubmit={onHandleSubmitWhatsApp}>
            <FormField
              control={whatsappForm.control}
              name="phone"
              render={({ field }) => (
                <Input
                  className={input()}
                  startIcon={faPhone}
                  placeholder="DDD + Número"
                  type="tel"
                  mask="(99) 99999-9999"
                  {...field}
                />
              )}
            />
            <Button startIcon={faCheck} className={buttonSubmit()} type="submit" />
          </form>
        </AccordionContent>
      </AccordionItem>
      <AccordionItem value="email" className={accordionItem()}>
        <AccordionTrigger className={accordionTrigger()} startIcon={false} showNoIcon={true} underlineOnHover={false}>
          <>
            <FontAwesomeIcon icon={faEnvelope} size="lg" />
            <Typography variant={TypographyVariant.BODY_TEXT} className="ml-2">Enviar por Email</Typography>
          </>
        </AccordionTrigger>
        <AccordionContent>
          <form className={form()} onSubmit={onHandleSubmitEmail}>
            <FormField
              control={emailForm.control}
              name="email"
              render={({ field }) => (
                <Input className={input()} type="email" placeholder="Digite o e-mail" {...field} />
              )}
            />
            <Button startIcon={faCheck} className={buttonSubmit()} isLoading={isLoading} type="submit" />
          </form>
        </AccordionContent>
      </AccordionItem>
      <Button
        className={buttonBack()}
        variant="secondary"
        onClick={() => jump({ key: AppointmentOptionsModalKeys.DEFAULT, returning: true })}
      >
        Voltar
      </Button>
    </Accordion>
  );
}
